/**
 * Login — Step 10. In production this page offers Entra ID (agents) and magic
 * link (requesters) once those doors are wired; in development it also shows
 * the dev door — the server 404s those endpoints in production, so the section
 * simply doesn't render there.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifeBuoy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../lib/AuthContext';
import { useUrlState } from '../lib/urlState';
import { useBranding, useDocumentTitle } from '../lib/useBranding';

interface DevUser {
  id: string;
  email: string;
  name: string | null;
  kind: string;
}

export function LoginPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [returnToParam] = useUrlState('returnTo', null);
  const [ssoErrorParam] = useUrlState('ssoError', null);
  const branding = useBranding();
  const tenantName = branding?.name ?? null;
  const appName = branding?.appName;
  const ssoAvailable = !!(branding as unknown as { sso?: boolean })?.sso;
  /** Step 155 — the door a tenant without Microsoft 365 uses instead. */
  const linkAvailable = !!(branding as unknown as { agentLinkSignIn?: boolean })?.agentLinkSignIn;
  const ssoError = ssoErrorParam;
  useDocumentTitle('Sign in');
  const [devUsers, setDevUsers] = useState<DevUser[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSent, setLinkSent] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);

  useEffect(() => {
    /**
     * Step 169 — the dev door is not merely hidden in production, it is not
     * BUILT. `import.meta.env.DEV` is a compile-time constant, so Vite drops
     * this call and the panel below from the production bundle entirely:
     * nothing to find in the JavaScript, nothing to probe. The server refuses
     * it too (loopback only, see `productionPosture.ts`) — two independent
     * mechanisms, because a single flag is what this step exists to remove.
     */
    if (!import.meta.env.DEV) return;
    // Bare fetch on purpose: the dev door is auth-domain plumbing — 404 or
    // 401 is an ANSWER, and apiFetch's redirect would loop the login page.
    fetch('/auth/dev/users', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => setDevUsers(b?.entries ?? null))
      .catch(() => setDevUsers(null));
  }, []);

  async function devLogin(email: string) {
    setBusy(true);
    setError(null);
    try {
      // Bare fetch on purpose: same auth-domain reasoning as the dev-users call.
      const res = await fetch('/auth/dev/login', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        setError(b?.error ?? `Sign-in failed (${res.status})`);
        return;
      }
      await refresh();
      // returnTo survives the round trip so an expired session lands you back
      // where you were, not on the dashboard.
      const returnTo = returnToParam;
      navigate(returnTo && returnTo.startsWith('/') ? returnTo : '/dashboard', { replace: true });
    } finally {
      setBusy(false);
    }
  }

  /**
   * Step 155 — ask for an emailed sign-in link.
   *
   * Bare fetch on purpose: this is auth-domain plumbing, exactly like the dev
   * door above. `apiFetch`'s 401 handling would redirect the login page to
   * itself.
   */
  async function requestLink() {
    setLinkBusy(true);
    setError(null);
    try {
      const res = await fetch('/auth/link/request', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: linkEmail.trim(), returnTo: returnToParam ?? undefined }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        setError(body?.error ?? `That did not work (${res.status})`);
        return;
      }
      // The server's sentence, which is deliberately the same whether or not
      // the address exists. Echoing it rather than writing our own keeps the
      // page from accidentally revealing what the API refused to.
      setLinkSent(body?.message ?? 'If that address can sign in here, a link is on its way.');
      // Dev only — the API returns this when it has no mail credentials.
      if (body?.devLink) console.log(`[login] DEV sign-in link: ${body.devLink}`);
    } finally {
      setLinkBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/15 text-brand">
            <LifeBuoy className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-semibold leading-tight">{tenantName ?? appName ?? '…'}</p>
            <p className="text-xs text-muted-foreground">Service desk sign in</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Sign in with Microsoft</CardTitle>
            <CardDescription>For agents and staff.</CardDescription>
          </CardHeader>
          <CardContent>
            {ssoAvailable ? (
              <Button
                className="w-full"
                onClick={() => {
                  const returnTo = returnToParam;
                  window.location.assign(
                    `/auth/sso/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`,
                  );
                }}
              >
                Sign in with Microsoft
              </Button>
            ) : (
              <Button
                className="w-full"
                disabled
                title="An admin can switch this on under Settings → Sign-in (SSO)"
              >
                Microsoft sign-in is not configured yet
              </Button>
            )}
            {ssoError && <p className="mt-2 text-xs text-destructive">{ssoError}</p>}
          </CardContent>
        </Card>

        {/* Step 155 — the door for a tenant with no Microsoft 365. Shown only
            when an admin has switched it on: advertising a door that does
            nothing is how somebody types their address into a form and waits
            for an email that was never going to arrive. */}
        {linkAvailable && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Email me a sign-in link</CardTitle>
              <CardDescription>
                {ssoAvailable ? 'If you do not use Microsoft to sign in.' : 'For agents and staff.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {linkSent ? (
                <p className="text-sm text-muted-foreground">{linkSent}</p>
              ) : (
                <form
                  className="space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void requestLink();
                  }}
                >
                  <Input
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    value={linkEmail}
                    onChange={(e) => setLinkEmail(e.target.value)}
                  />
                  <Button type="submit" className="w-full" disabled={linkBusy || !linkEmail.trim()}>
                    {linkBusy ? 'Sending…' : 'Send me a link'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {import.meta.env.DEV && devUsers && (
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-sm">
                Development door <Badge variant="secondary">dev only</Badge>
              </CardTitle>
              <CardDescription>These endpoints do not exist in production builds.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {devUsers.map((u) => (
                <Button
                  key={u.id}
                  variant="outline"
                  className="w-full justify-between"
                  disabled={busy}
                  onClick={() => devLogin(u.email)}
                >
                  <span className="truncate">{u.name ?? u.email}</span>
                  <Badge variant="secondary">{u.kind}</Badge>
                </Button>
              ))}
              {error && <p className="text-xs text-destructive">{error}</p>}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
